<template>
  <div :class="b()">
    <c-modal
      v-model:open="isOpen"
      :size="$viewport.isMd ? 300 : 500"
      :click-background-to-close="clickBackgroundToClose"
      @close="onClose"
    >
      <h2>
        {{ title }}
      </h2>
      <div :class="b('content')">
        {{ text }}
      </div>
      <div :class="b('footer')">
        <!-- Confirmation button -->
        <e-button
          :progress="progress"
          :disabled="progress"
          width="full"
          primary
          variant="primary"
          @click.stop="onConfirm"
        >
          {{ confirmButtonText }}
        </e-button>

        <!-- Cancel Button -->
        <e-button
          v-if="hasCancelButton"
          :disabled="progress"
          width="full"
          @click.stop="onCancel"
        >
          {{ cancelButtonText }}
        </e-button>
      </div>
    </c-modal>
  </div>
</template>

<script>
  import cModal from '@/components/c-modal.vue';

  /**
   * Confirmation modal component is used to show the confirmation dialog in a modal.
   */
  export default {
    name: 'c-confirmation-modal',

    components: {
      cModal,
    },
    // mixins: [],

    // props: {},
    data() {
      return {
        /**
         * @type {Boolean} Flag defines if the modal is currently open.
         */
        isOpen: false,

        /**
         * @type {String} Defines the title of the modal.
         */
        title: '',

        /**
         * @type {String} Defines the content text of the modal.
         */
        text: '',

        /**
         * @type {String} Defines confirmation button text.
         */
        confirmButtonText: '',

        /**
         * @type {String} Defines the cancel button text.
         */
        cancelButtonText: '',

        /**
         * @type {Boolean} Defines if the confirmation can be canceled.
         */
        hasCancelButton: true,

        /**
         * @type {Boolean} Defines if the background click closes the modal.
         */
        clickBackgroundToClose: true,

        /**
         * @type {Boolean} Flag that holds the progress state.
         */
        progress: false,
      };
    },

    // computed: {},
    watch: {
      /**
       * Observes "confirmation" attribute in the $confirmation plugin.
       * Set's the data for the modal and opens the modal if it's filled correctly.
       *
       * @param {Object} value - The confirmation object with its options.
       */
      '$confirmation.confirmation': {
        handler(value) {
          const { options } = value || {};

          if (options) {
            this.setData(options);
          }

          this.isOpen = !!value;
        },
        deep: true,
      },
    },

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      /**
       * Sets the data for the modal.
       *
       * @param {Object} options - The confirmation modal instance data.
       * @param {String} options.title - The confirmation modal title.
       * @param {String} [options.text] - The confirmation modal content text.
       * @param {String} options.confirmText - The label for the confirm button.
       * @param {String} [options.cancelText] - The label for the cancel button.
       * @param {Boolean} [options.hasCancelButton = true] - Defines if the modal has a cancel button.
       */
      setData(options = {}) {
        const {
          title,
          text,
          confirmText = this.$t('c-confirmation-modal.confirm'),
          cancelText = this.$t('c-confirmation-modal.cancel'),
          hasCancelButton,
          clickBackgroundToClose = true,
        } = options;

        this.title = title;
        this.text = text;
        this.confirmButtonText = confirmText;
        this.cancelButtonText = cancelText;
        this.clickBackgroundToClose = clickBackgroundToClose;
        this.hasCancelButton = hasCancelButton !== false;
      },

      /**
       * Confirmation event handler.
       */
      onConfirm() {
        const callback = this.$confirmation?.confirmation?.options?.successCallback;

        if (callback) {
          this.progress = true;
        }

        new Promise(callback || ((resolve) => resolve()))
          .then(() => {
            this.$confirmation?.confirmation?.resolve();
          })
          .finally(() => {
            this.progress = false;
          });
      },

      /**
       * Cancel event handler.
       */
      onCancel() {
        if (this.$confirmation.confirmation) {
          this.$confirmation.confirmation.reject();
        }
      },

      /**
       * Callback for the close event of the modal.
       */
      onClose() {
        if (this.hasCancelButton) {
          this.onCancel();
        } else {
          this.onConfirm();
        }
      },
    },
    // render() {},
  };
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  // @use '@/setup/scss/extends' as *;
  @use '@/setup/scss/functions';

  .c-confirmation-modal {
    &__content {
      @include mixins.font(variables.$font-size--18);

      margin: functions.rem(variables.$spacing--30 0 variables.$spacing--50);
      white-space: pre-line;
    }

    &__footer {
      @include mixins.media(sm) {
        display: flex;
        justify-content: space-between;
      }

      .e-button {
        margin-bottom: functions.rem(variables.$spacing--15);

        @include mixins.media(sm) {
          flex-grow: 1 0 calc(50% - #{functions.rem(variables.$spacing--10)});
          max-width: calc(50% - #{functions.rem(variables.$spacing--10)});
          margin-bottom: 0;
        }
      }

      .e-button:first-of-type {
        @include mixins.media(sm) {
          order: 2;
        }
      }
    }
  }
</style>
